import React, { useState } from 'react';
import { WidgetCatalogCategory, WidgetCatalogItem, PresentationWidgetType, MasterReportTemplate } from '../../types';
import { loadMasterReportTemplates } from '../admin/ReportTemplateManager';

/**
 * Static catalog of available widgets, organized by project area.
 */
export const WIDGET_CATALOG: WidgetCatalogCategory[] = [
    {
        id: 'maestra',
        label: 'Maestra & Intermedia',
        icon: 'calendar_month',
        color: '#1e40af',
        items: [
            { type: 'gantt_master', label: 'Carta Gantt Maestra', icon: 'calendar_month', description: 'Diagrama de barras de la planificación maestra', defaultWidth: 60, defaultHeight: 40 },
            { type: 's_curve', label: 'Curva S (Avance Físico)', icon: 'trending_up', description: 'Curva S de avance acumulado (Programado vs Real)', defaultWidth: 60, defaultHeight: 45 },
            { type: 'milestones', label: 'Hitos del Proyecto', icon: 'flag', description: 'Lista de hitos y fechas clave', defaultWidth: 35, defaultHeight: 30 },
            { type: 'lookahead', label: 'Lookahead', icon: 'view_timeline', description: 'Resumen de la planificación intermedia', defaultWidth: 45, defaultHeight: 35 },
        ],
    },
    {
        id: 'semanal',
        label: 'Semanal',
        icon: 'view_week',
        color: '#0369a1',
        items: [
            { type: 'weekly_prev_full', label: 'Plan. Anterior (Completa)', icon: 'history', description: 'Planificación semanal anterior completa', defaultWidth: 92, defaultHeight: 85 },
            { type: 'weekly_prev_executed', label: 'Plan. Anterior (Ejecutada)', icon: 'check_circle', description: 'Tareas ejecutadas de la semana anterior', defaultWidth: 92, defaultHeight: 85 },
            { type: 'weekly_prev_not_executed', label: 'Plan. Anterior (No Ejecutadas)', icon: 'cancel', description: 'Tareas no ejecutadas de la semana anterior', defaultWidth: 92, defaultHeight: 85 },
            { type: 'weekly_prev_cnc', label: 'Causas No Cumplimiento', icon: 'report_problem', description: 'CNC de la semana anterior', defaultWidth: 40, defaultHeight: 30 },
            { type: 'weekly_current', label: 'Planificación Actual', icon: 'edit_calendar', description: 'Planificación semanal activa', defaultWidth: 92, defaultHeight: 85 },
            { type: 'graphic_progress_current', label: 'Avance Gráfico Actual', icon: 'map', description: 'Plano de avance gráfico de la semana actual', defaultWidth: 92, defaultHeight: 85 },
            { type: 'graphic_progress_prev', label: 'Avance Gráfico Anterior', icon: 'map', description: 'Plano de avance gráfico de la semana anterior', defaultWidth: 92, defaultHeight: 85 },
        ],
    },
    {
        id: 'calidad',
        label: 'Calidad & Subcontratos',
        icon: 'fact_check',
        color: '#7e22ce',
        items: [
            { type: 'protocol_summary', label: 'Resumen Protocolos', icon: 'fact_check', description: 'Estado resumido de protocolos de calidad', defaultWidth: 35, defaultHeight: 30 },
            { type: 'compliance_semaphore', label: 'Semáforos de Cumplimiento', icon: 'traffic', description: 'Semáforos visuales de cumplimiento', defaultWidth: 30, defaultHeight: 25 },
            { type: 'sc_evaluations', label: 'Evaluaciones SC', icon: 'assessment', description: 'Matriz de evaluación y notas de subcontratistas', defaultWidth: 80, defaultHeight: 65 },
        ],
    },
    {
        id: 'reportes',
        label: 'Reportes',
        icon: 'bar_chart',
        color: '#be123c',
        items: [
            { type: 'pareto_chart', label: 'Gráfico Pareto CNC', icon: 'bar_chart', description: 'Diagrama de Pareto para causas de no cumplimiento', defaultWidth: 40, defaultHeight: 35 },
            { type: 'ppc_evolution', label: 'Evolución PPC / PCR', icon: 'trending_up', description: 'Gráfico de evolución del PPC y PCR semanal', defaultWidth: 45, defaultHeight: 35 },
            { type: 'kpi_cards', label: 'Tarjetas KPIs', icon: 'dashboard', description: 'Panel de indicadores clave del proyecto', defaultWidth: 40, defaultHeight: 35 },
        ],
    },
    {
        id: 'diseno',
        label: 'Diseño y Formatos',
        icon: 'palette',
        color: '#d97706',
        items: [
            { type: 'title_block', label: 'Título / Encabezado', icon: 'title', description: 'Título estilizado con tipografía, color y tamaño configurable', defaultWidth: 50, defaultHeight: 12 },
            { type: 'text_block', label: 'Caja de Texto', icon: 'notes', description: 'Cuadro de texto libre con formato enriquecido', defaultWidth: 40, defaultHeight: 25 },
            { type: 'image_block', label: 'Imagen / Logo', icon: 'image', description: 'Subir o insertar imagen, render o logotipo de obra', defaultWidth: 35, defaultHeight: 30 },
            { type: 'shape_block', label: 'Forma Geométrica', icon: 'shapes', description: 'Rectángulo, tarjeta, píldora, círculo o línea decorativa', defaultWidth: 30, defaultHeight: 20 },
        ],
    },
];

interface WidgetCatalogProps {
    onAddWidget: (type: PresentationWidgetType, defaultWidth: number, defaultHeight: number) => void;
    onAddReportTemplate?: (template: MasterReportTemplate) => void;
    onClose: () => void;
}

/**
 * Sidebar/panel component that displays all available widgets grouped by category.
 * Clicking on a widget item triggers onAddWidget or onAddReportTemplate.
 */
const WidgetCatalog: React.FC<WidgetCatalogProps> = ({ onAddWidget, onAddReportTemplate, onClose }) => {
    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(
        Object.fromEntries(WIDGET_CATALOG.map(c => [c.id, true]))
    );

    const reportTemplates = React.useMemo(() => {
        return loadMasterReportTemplates().filter(t => t.id !== 'tpl-blank');
    }, []);

    const toggleCategory = (id: string) => {
        setExpandedCategories(prev => ({ ...prev, [id]: !prev[id] }));
    };

    return (
        <div style={{
            width: '280px',
            height: '100%',
            background: '#f8fafc',
            borderLeft: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <div style={{
                padding: '14px 16px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#fff',
                flexShrink: 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#3b82f6' }}>add_circle</span>
                    <span style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Añadir Widget</span>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                        borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#94a3b8' }}>close</span>
                </button>
            </div>

            {/* Categories */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                {WIDGET_CATALOG.map(category => (
                    <div key={category.id} style={{ marginBottom: '4px' }}>
                        {/* Category Header */}
                        <button
                            onClick={() => toggleCategory(category.id)}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                textAlign: 'left',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                        >
                            <span
                                className="material-symbols-outlined"
                                style={{
                                    fontSize: '18px',
                                    color: category.color,
                                    background: `${category.color}15`,
                                    borderRadius: '6px',
                                    padding: '4px',
                                }}
                            >
                                {category.icon}
                            </span>
                            <span style={{ flex: 1, fontWeight: 600, fontSize: '12px', color: '#334155' }}>
                                {category.label}
                            </span>
                            <span style={{ fontSize: '10px', color: '#94a3b8', background: '#e2e8f0', borderRadius: '10px', padding: '1px 7px', fontWeight: 600 }}>
                                {category.items.length}
                            </span>
                            <span
                                className="material-symbols-outlined"
                                style={{
                                    fontSize: '16px',
                                    color: '#94a3b8',
                                    transform: expandedCategories[category.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }}
                            >
                                keyboard_arrow_down
                            </span>
                        </button>

                        {/* Category Items */}
                        {expandedCategories[category.id] && (
                            <div style={{ paddingLeft: '16px', paddingRight: '12px' }}>
                                {category.id === 'reportes' && reportTemplates.length > 0 && (
                                    <div style={{ marginBottom: '10px' }}>
                                        <div style={{
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            color: '#be123c',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '4px 2px 6px 2px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>auto_stories</span>
                                            Plantillas de Reporte
                                        </div>
                                        {reportTemplates.map(tpl => {
                                            const pageCount = (tpl as any).metadata?.reportPages?.length || 
                                                new Set(tpl.widgets.map((w: any) => w.pageId || 'page-1')).size || 1;
                                            return (
                                                <button
                                                    key={tpl.id}
                                                    onClick={() => onAddReportTemplate ? onAddReportTemplate(tpl) : null}
                                                    style={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        padding: '8px 12px',
                                                        background: '#fff',
                                                        border: '1px solid #fecdd3',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        marginBottom: '5px',
                                                        textAlign: 'left',
                                                        transition: 'all 0.15s',
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.borderColor = '#e11d48';
                                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(225,29,72,0.15)';
                                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.borderColor = '#fecdd3';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                        e.currentTarget.style.transform = 'none';
                                                    }}
                                                >
                                                    <span
                                                        className="material-symbols-outlined"
                                                        style={{ fontSize: '18px', color: '#e11d48', flexShrink: 0 }}
                                                    >
                                                        {tpl.icon || 'analytics'}
                                                    </span>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                            <span style={{ fontSize: '11px', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {tpl.name}
                                                            </span>
                                                            {pageCount > 1 && (
                                                                <span style={{
                                                                    fontSize: '8.5px',
                                                                    padding: '1px 5px',
                                                                    borderRadius: '4px',
                                                                    background: '#ffe4e6',
                                                                    color: '#e11d48',
                                                                    fontWeight: 700,
                                                                    flexShrink: 0
                                                                }}>
                                                                    {pageCount} diapositivas
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {tpl.description}
                                                        </div>
                                                    </div>
                                                    <span className="material-symbols-outlined" style={{ fontSize: '14px', color: '#fda4af' }}>open_in_new</span>
                                                </button>
                                            );
                                        })}
                                        <div style={{
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            color: '#64748b',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 2px 4px 2px',
                                            borderTop: '1px dashed #e2e8f0',
                                            marginTop: '6px',
                                        }}>
                                            Widgets Individuales
                                        </div>
                                    </div>
                                )}
                                {category.items.map(item => (
                                    <button
                                        key={item.type}
                                        onClick={() => onAddWidget(item.type, item.defaultWidth, item.defaultHeight)}
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            padding: '8px 12px',
                                            background: '#fff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            marginBottom: '4px',
                                            textAlign: 'left',
                                            transition: 'all 0.15s',
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.borderColor = category.color;
                                            e.currentTarget.style.boxShadow = `0 2px 8px ${category.color}20`;
                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.boxShadow = 'none';
                                            e.currentTarget.style.transform = 'none';
                                        }}
                                    >
                                        <span
                                            className="material-symbols-outlined"
                                            style={{ fontSize: '16px', color: category.color }}
                                        >
                                            {item.icon}
                                        </span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.label}
                                            </div>
                                            <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.description}
                                            </div>
                                        </div>
                                        <span className="material-symbols-outlined" style={{ fontSize: '14px', color: '#cbd5e1' }}>add</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Footer hint */}
            <div style={{
                padding: '10px 16px',
                borderTop: '1px solid #e2e8f0',
                background: '#fff',
                fontSize: '10px',
                color: '#94a3b8',
                textAlign: 'center',
                flexShrink: 0,
            }}>
                Haga clic en un widget para añadirlo a la diapositiva activa
            </div>
        </div>
    );
};

export default WidgetCatalog;
